import { Trans, useTranslation } from 'react-i18next';

import QuestionImage from 'assets/images/question.svg';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { Delimiter } from 'components/delimiter';
import { Buttons, Image } from 'components/modal/styles';

import { formatCurrencySymbol, formatNumberI18n } from 'utils/i18n';
import { formatFxRate } from 'utils/refundFx';

import { LABELS } from './keys';
import { BoldStatement, ModalWithImageContent } from './styles';

interface ConfirmationDialogProps {
  fiatAmount: number;
  currencyCode: string;
  settlementAmount?: number;
  settlementCurrencyCode?: string;
  displayFxRate?: string | null;
  isEstimateLoading: boolean;
  isEstimateUnavailable: boolean;
  isLoading: boolean;
  onCancel: () => void;
  onProceed: () => void;
}

const ConfirmationDialog = ({
  fiatAmount,
  currencyCode,
  settlementAmount,
  settlementCurrencyCode,
  displayFxRate,
  isEstimateLoading,
  isEstimateUnavailable,
  isLoading,
  onCancel,
  onProceed,
}: ConfirmationDialogProps) => {
  const { t, i18n } = useTranslation();
  const numberFormatter = formatNumberI18n(i18n.language);
  const formattedSymbol = formatCurrencySymbol(i18n.language)(currencyCode);
  const formattedSettlementAmount = settlementAmount ? numberFormatter(settlementAmount) : undefined;
  const formattedDisplayFxRate = displayFxRate ? formatFxRate(displayFxRate, i18n.language) : undefined;
  const hasSettlementEstimate = Boolean(formattedSettlementAmount && settlementCurrencyCode && formattedDisplayFxRate);

  return (
    <>
      <ModalWithImageContent>
        <Image src={QuestionImage} />
        <h1>{t(LABELS.QUESTION)}</h1>
        <p>
          <Trans
            i18nKey={LABELS.REMARK[0]}
            defaults={LABELS.REMARK[1]}
            values={{
              amountWithCurrency: `${numberFormatter(fiatAmount)} ${formattedSymbol}`,
            }}
            components={{
              b: <BoldStatement />,
              br: <br />,
            }}
          />
          {hasSettlementEstimate && (
            <Trans
              i18nKey={LABELS.ESTIMATE[0]}
              defaults={LABELS.ESTIMATE[1]}
              values={{
                settlementAmount: formattedSettlementAmount,
                settlementCurrencyCode,
                displayFxRate: formattedDisplayFxRate,
                fiatCurrencyCode: currencyCode.toUpperCase(),
              }}
              components={{
                b: <BoldStatement />,
                br: <br />,
              }}
            />
          )}
          {isEstimateLoading && (
            <Trans i18nKey={LABELS.ESTIMATE_LOADING[0]} defaults={LABELS.ESTIMATE_LOADING[1]} components={{ br: <br /> }} />
          )}
          {isEstimateUnavailable && (
            <Trans i18nKey={LABELS.ESTIMATE_UNAVAILABLE[0]} defaults={LABELS.ESTIMATE_UNAVAILABLE[1]} components={{ br: <br /> }} />
          )}
        </p>
      </ModalWithImageContent>
      <Delimiter />
      <Buttons>
        <Button secondary onClick={onCancel}>
          {t(LABELS.ACTIONS.BACK)}
        </Button>
        <ButtonWithLoading primary disabled={isEstimateLoading} isLoading={isLoading} onClick={onProceed}>
          {t(LABELS.ACTIONS.CONFIRM)}
        </ButtonWithLoading>
      </Buttons>
    </>
  );
};

export default ConfirmationDialog;
